/**
 * A URL layer's EXIT (`D193`).
 *
 * A sheet or an overlay is open because the URL says so, and the shell used
 * to unmount it the moment the URL stopped saying so — before Base UI could
 * play the ending style every popup declares. Back, the X and a swipe all
 * made the layer vanish. The shell now keeps a closed layer mounted while it
 * leaves (`app/shell.tsx`), and tells it so through this context:
 *
 * - `present` is false while the layer plays its exit. The layer's Base UI
 *   root binds `open` to it, so the popup gets `data-ending-style` and runs
 *   the same transition it would for a close it started itself.
 * - `frozen` is the search the layer last saw while it was present. An
 *   exiting layer re-renders under the NEW url — `ex` already gone — and
 *   would draw "exercise not found" while it slid away; `useAppSearch` reads
 *   this instead while it is set.
 * - `done` tells the shell the exit has finished and the layer may unmount.
 *   Exactly ONE primitive calls it: the first one under the context, the
 *   layer's own root. A sheet nested inside an overlay closes with it but
 *   does not report, or the overlay would be removed mid-exit when its
 *   child's (shorter) exit ended first. `owned` is how a nested primitive
 *   knows it is not the root.
 * - `slot` marks the root's popup (`data-layer`), which is how the
 *   predictive Back finds the layer a gesture should move.
 * - `covered` is true while a sheet pushed over this one sits on top of it
 *   (`D225`). The layer stays open and drawn, exactly as it was, but it is
 *   `inert`, loses its `data-layer` mark (the gesture moves the top one),
 *   reads its own `frozen` search, and asks nothing of the URL.
 *
 * Outside a URL layer — a story, an ephemeral sheet opened from a screen —
 * the default answers "present, not owned", and nothing changes.
 */
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";

export type LayerSlot = "overlay" | "sheet";

export interface LayerPresenceValue {
  /** A layer pushed later in the same slot covers this one (`D225`). */
  readonly covered: boolean;
  /** Call once the exit has finished; the shell then unmounts the layer. */
  readonly done: () => void;
  /** The search the layer saw last on top; set while exiting or covered. */
  readonly frozen: unknown;
  /** A primitive above already owns this layer's exit. */
  readonly owned: boolean;
  /** False while the layer plays its exit. */
  readonly present: boolean;
  /** Which URL slot the layer fills; `null` outside one. */
  readonly slot: LayerSlot | null;
}

const NOTHING = () => undefined;

const OUTSIDE: LayerPresenceValue = {
  covered: false,
  done: NOTHING,
  frozen: undefined,
  owned: false,
  present: true,
  slot: null,
};

const LayerPresenceContext = createContext<LayerPresenceValue>(OUTSIDE);

export const LayerPresenceProvider = LayerPresenceContext.Provider;

export function useLayerPresence(): LayerPresenceValue {
  return useContext(LayerPresenceContext);
}

/** Marks everything below as inside a layer whose exit is already owned. */
export function LayerOwned({ children }: { children: ReactNode }) {
  const presence = useLayerPresence();
  const value = useMemo(
    () => (presence.owned ? presence : { ...presence, owned: true }),
    [presence]
  );
  return (
    <LayerPresenceContext.Provider value={value}>
      {children}
    </LayerPresenceContext.Provider>
  );
}

/**
 * What a layer's root popup carries (`D193`, `D225`): the slot mark the
 * predictive Back looks for — dropped while covered, so the gesture finds
 * the layer on top — and `inert` unless on top.
 */
export function rootMarks(
  slot: LayerSlot | null,
  present: boolean,
  covered: boolean
): { readonly "data-layer"?: LayerSlot; readonly inert?: boolean } {
  return {
    "data-layer": covered ? undefined : (slot ?? undefined),
    inert: present && !covered ? undefined : true,
  };
}

export interface LayerRoot {
  /** Bind to the Base UI root's `onOpenChangeComplete`. */
  readonly onOpenChangeComplete: (open: boolean) => void;
  /** Bind to the Base UI root's `open`. */
  readonly open: boolean;
  /** Whether this root reports the exit — false when nested in another layer. */
  readonly owner: boolean;
  /** Spread on the root's popup: `inert` unless on top, and the slot mark. */
  readonly popupProps: {
    readonly "data-layer"?: LayerSlot;
    readonly inert?: boolean;
  };
}

/**
 * What a layer's Base UI root binds, for a feature that draws its own
 * `Dialog` rather than `Sheet` or `FullScreenLayer` (profile, search,
 * wrapped, body, the trainer). Wrap the popup's children in `LayerOwned`, so
 * a sheet inside does not report the exit for it.
 *
 * `wanted` is the open the feature would bind without the shell — `true` for
 * a URL layer, local state for anything else.
 */
export function useLayerRoot(wanted = true): LayerRoot {
  const presence = useLayerPresence();
  const owner = !presence.owned && presence.slot !== null;
  const { covered, done, present, slot } = presence;
  const onOpenChangeComplete = useCallback(
    (open: boolean) => {
      if (!open && owner) {
        done();
      }
    },
    [done, owner]
  );
  const popupProps = owner ? rootMarks(slot, present, covered) : {};
  return {
    onOpenChangeComplete,
    open: wanted && present,
    owner,
    popupProps,
  };
}
